import React, { useEffect } from 'react';
import { Box, InputAdornment, Typography } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import TvIcon from '@mui/icons-material/Tv';
import i18next from 'i18next';
import { Control, useFormContext } from 'react-hook-form';

import { FormInputCheckbox } from 'components/form/FormInputCheckbox';
import { FormInputDropdown } from 'components/form/FormInputDropdown';
import { FormInputNumber } from 'components/form/FormInputNumber';
import { FormInputText } from 'components/form/FormInputText';
import { CustomIcon, IconName } from 'components/ui/CustomIcon';
import { OfferVersionFormType, SeminarRoomRequest } from './context';

const seatingIcons: Record<string, IconName> = {
  UFORM: 'uform',
  THEATER: 'theater',
  PARLAMENT: 'parlament',
  CIRCLE: 'kreis',
  BANKETT: 'bankett',
  COCKTAIL: 'cocktail',
  BLOCK: 'block',
};

const iconSx = { width: 16, height: 16, fontSize: 16 };
const techCount = (value?: number | null) => value ?? 1;

/**
 * Hotel confirmation for one seminar-room line.
 * Requested characteristics are read-only and must be confirmed. An extra room can set every characteristic.
 */
const SeminarRoomOfferFields = ({
  namePrefix,
  control,
  disabled,
  request,
  header,
}: {
  namePrefix: `contentBlocks.${number}.lineItems.${number}`;
  control: Control<OfferVersionFormType>;
  disabled: boolean;
  request?: SeminarRoomRequest | null;
  header: string;
}) => {
  const isExtra = !request;
  const showAir = isExtra || !!request?.requireAirConditioning;
  const showDaylight = isExtra || !!request?.requireDaylight;
  const minimum = request?.minRoomAreaSqm || 0;
  const showAcknowledgement = isExtra || showAir || showDaylight || minimum > 0;
  const tech = [
    { icon: <TvIcon sx={iconSx} />, label: i18next.t('selector-lister-beamer'), count: techCount(request?.beamerCount), name: 'offeredBeamerCount' as const },
    { icon: <InfoOutlinedIcon sx={iconSx} />, label: i18next.t('selector-lister-pinnwand'), count: techCount(request?.pinboardCount), name: 'offeredPinboardCount' as const },
    { icon: <ArticleOutlinedIcon sx={iconSx} />, label: i18next.t('selector-lister-flipchart'), count: techCount(request?.flipchartCount), name: 'offeredFlipchartCount' as const },
  ];
  const seating = request?.seating || '';
  const seatingOptions = (Object.keys(seatingIcons) as (keyof typeof seatingIcons)[]).map(value => ({
    value,
    label: i18next.t(`enums-EOfferRoomSeating-${value}`),
  }));
  const { getValues, setValue } = useFormContext<OfferVersionFormType>();

  useEffect(() => {
    if (disabled || isExtra || minimum > 0) return;
    if (getValues(`${namePrefix}.guaranteedRoomAreaSqm`) == null) {
      setValue(`${namePrefix}.guaranteedRoomAreaSqm`, 0, { shouldDirty: false, shouldValidate: false });
    }
  }, [disabled, getValues, isExtra, minimum, namePrefix, setValue]);

  useEffect(() => {
    if (disabled || !isExtra) return;
    if (!getValues(`${namePrefix}.offeredSeating`)) {
      setValue(`${namePrefix}.offeredSeating`, 'UFORM', { shouldDirty: false, shouldValidate: false });
    }
  }, [disabled, getValues, isExtra, namePrefix, setValue]);

  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1, mt: 1 }}>
      {!isExtra && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 6, mb: 1.5 }}>
          <Typography variant="body2">{header}</Typography>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            <PeopleAltOutlinedIcon sx={iconSx} />
            <Typography variant="body2">
              {request?.seatCount ?? 0} {i18next.t('offer-room-persons')}
            </Typography>
          </Box>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            <AccessTimeIcon sx={iconSx} />
            <Typography variant="body2">
              {request?.occupancyFrom} – {request?.occupancyTo}
            </Typography>
          </Box>
          {seatingIcons[seating] && (
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
              <CustomIcon name={seatingIcons[seating]} sx={iconSx} />
              <Typography variant="body2">{i18next.t(`enums-EOfferRoomSeating-${seating}`)}</Typography>
            </Box>
          )}
        </Box>
      )}
      {isExtra && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 1 }}>
          <Typography variant="body2">{header}</Typography>
            <PeopleAltOutlinedIcon sx={iconSx} />
            <Box sx={{ width: 72 }}>
              <FormInputNumber name={`${namePrefix}.offeredSeatCount`} control={control} disabled={disabled} textFieldProps={{ size: 'small' }} />
            </Box>
            <Typography variant="body2">{i18next.t('offer-room-persons')}</Typography>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap' }}>
              <AccessTimeIcon sx={iconSx} />
              <Box sx={{ width: 150 }}>
                <FormInputText name={`${namePrefix}.offeredFrom`} control={control} disabled={disabled} textFieldProps={{ size: 'small', type: 'time' }} />
              </Box>
              <Typography variant="body2">–</Typography>
              <Box sx={{ width: 150 }}>
                <FormInputText name={`${namePrefix}.offeredTo`} control={control} disabled={disabled} textFieldProps={{ size: 'small', type: 'time' }} />
              </Box>
            </Box>
            <Box sx={{ minWidth: 140 }}>
              <FormInputDropdown name={`${namePrefix}.offeredSeating`} control={control} disabled={disabled} options={seatingOptions} />
            </Box>
        </Box>
      )}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.6fr) minmax(0, 1fr)', columnGap: 3, rowGap: 1.5, alignItems: 'center' }}>
        {tech.map((item, index) => (
          <React.Fragment key={item.name}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
              {item.icon}
              {isExtra ? (
                <Box sx={{ width: 64 }}>
                  <FormInputNumber name={`${namePrefix}.${item.name}`} control={control} disabled={disabled} textFieldProps={{ size: 'small' }} />
                </Box>
              ) : null}
              <Typography variant="body2" noWrap title={String(item.label)} sx={{ minWidth: 0 }}>
                {isExtra ? item.label : `${item.count}x ${item.label}`}
              </Typography>
            </Box>
            <Box sx={{ minWidth: 0 }}>
              {index === 0 && (
                <Box sx={{ maxWidth: 220 }}>
                  <FormInputNumber
                    name={`${namePrefix}.guaranteedRoomAreaSqm`}
                    label={i18next.t('offer-room-guaranteed-size')}
                    control={control}
                    disabled={disabled}
                    helperText={minimum > 0 ? i18next.t('offer-room-min-size-hint', { size: minimum }) : undefined}
                    textFieldProps={{
                      size: 'small',
                      InputProps: { endAdornment: <InputAdornment position="end" sx={{ mr: 1 }}>m²</InputAdornment> },
                    }}
                  />
                </Box>
              )}
              {index === 1 && showAir && (
                <FormInputCheckbox
                  name={`${namePrefix}.confirmAirConditioning`}
                  label={i18next.t('offer-occupancy-air')}
                  control={control}
                  disabled={disabled}
                />
              )}
              {index === 2 && showDaylight && (
                <FormInputCheckbox
                  name={`${namePrefix}.confirmDaylight`}
                  label={i18next.t('offer-occupancy-daylight')}
                  control={control}
                  disabled={disabled}
                />
              )}
            </Box>
          </React.Fragment>
        ))}
      </Box>
      {showAcknowledgement && (
        <Box sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}>
          <FormInputCheckbox
            name={`${namePrefix}.requirementsAcknowledged`}
            label={i18next.t('offer-room-acknowledge')}
            control={control}
            disabled={disabled}
          />
        </Box>
      )}
    </Box>
  );
};

export default SeminarRoomOfferFields;
